import React from 'react';
import { Text, Section, Link } from '@react-email/components';
import Layout, { LayoutTranslations } from './components/Layout';

export interface NewAppointmentRequestTranslations {
  title: string;
  greeting: string;
  body: string;
  visitorInformation: string;
  labelName: string;
  labelEmail: string;
  labelPosition: string;
  labelCompany: string;
  requestedSlot: string;
  labelDate: string;
  labelTime: string;
  visitorMessage: string;
  action: string;
  ctaText: string;
  signOff: string;
  teamSignature: string;
  layout: LayoutTranslations;
}

interface NewAppointmentRequestProps {
  exhibitorName: string;
  visitorName: string;
  visitorEmail: string;
  visitorCompany?: string;
  visitorPosition?: string;
  date: string;
  time: string;
  message?: string;
  translations: NewAppointmentRequestTranslations;
}

export default function NewAppointmentRequest({
  exhibitorName,
  visitorName,
  visitorEmail,
  visitorCompany,
  visitorPosition,
  date,
  time,
  message,
  translations: tx,
}: NewAppointmentRequestProps) {
  return (
    <Layout previewText={`${tx.title} - ${visitorName}`} translations={tx.layout}>
      <Text style={heading}>{tx.title}</Text>
      <Text style={paragraph}>{tx.greeting} {exhibitorName},</Text>
      <Text style={paragraph}>{tx.body.replace('{{name}}', visitorName)}</Text>

      <Section style={visitorBox}>
        <Text style={visitorBoxTitle}>{tx.visitorInformation}</Text>
        <Text style={infoLine}>{tx.labelName}: {visitorName}</Text>
        <Text style={infoLine}>{tx.labelEmail}: {visitorEmail}</Text>
        {visitorPosition && <Text style={infoLine}>{tx.labelPosition}: {visitorPosition}</Text>}
        {visitorCompany && <Text style={infoLine}>{tx.labelCompany}: {visitorCompany}</Text>}
      </Section>

      <Section style={infoBox}>
        <Text style={infoBoxTitle}>{tx.requestedSlot}</Text>
        <Text style={infoLine}>{tx.labelDate}: {date}</Text>
        <Text style={infoLine}>{tx.labelTime}: {time}</Text>
      </Section>

      {message && (
        <Section style={messageBox}>
          <Text style={messageLabel}>{tx.visitorMessage}:</Text>
          <Text style={messageText}>{message}</Text>
        </Section>
      )}

      <Text style={paragraph}>{tx.action}</Text>

      <Section style={ctaContainer}>
        <Link href="https://siportevent.com/dashboard/appointments" style={ctaButton}>{tx.ctaText}</Link>
      </Section>

      <Text style={paragraph}>{tx.signOff}<br />{tx.teamSignature}</Text>
    </Layout>
  );
}

const heading: React.CSSProperties = { fontSize: '24px', fontWeight: 700, color: '#1e40af', margin: '0 0 20px 0' };
const paragraph: React.CSSProperties = { fontSize: '16px', lineHeight: '1.6', color: '#374151', margin: '0 0 16px 0' };
const visitorBox: React.CSSProperties = { backgroundColor: '#eff6ff', borderLeft: '4px solid #3b82f6', padding: '16px 20px', margin: '20px 0', borderRadius: '0 4px 4px 0' };
const visitorBoxTitle: React.CSSProperties = { fontSize: '14px', fontWeight: 600, color: '#1e40af', margin: '0 0 12px 0', textTransform: 'uppercase' as const, letterSpacing: '0.5px' };
const infoBox: React.CSSProperties = { backgroundColor: '#f0fdf4', borderLeft: '4px solid #22c55e', padding: '16px 20px', margin: '20px 0', borderRadius: '0 4px 4px 0' };
const infoBoxTitle: React.CSSProperties = { fontSize: '14px', fontWeight: 600, color: '#166534', margin: '0 0 12px 0', textTransform: 'uppercase' as const, letterSpacing: '0.5px' };
const infoLine: React.CSSProperties = { fontSize: '15px', color: '#374151', margin: '0 0 6px 0' };
const messageBox: React.CSSProperties = { backgroundColor: '#fefce8', border: '1px solid #fde68a', padding: '16px 20px', margin: '20px 0', borderRadius: '4px' };
const messageLabel: React.CSSProperties = { fontSize: '13px', fontWeight: 600, color: '#92400e', margin: '0 0 8px 0' };
const messageText: React.CSSProperties = { fontSize: '15px', color: '#78350f', margin: 0, whiteSpace: 'pre-wrap' as const };
const ctaContainer: React.CSSProperties = { textAlign: 'center' as const, margin: '30px 0' };
const ctaButton: React.CSSProperties = { display: 'inline-block', padding: '14px 28px', backgroundColor: '#2563eb', color: '#ffffff', textDecoration: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '16px' };
